import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Sparkles, Target, Send, Package, Globe, ArrowRight,
    ArrowLeft, X, Check, CheckCircle2, Loader2, PartyPopper,
    Rocket,
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TOTAL_STEPS = 8;

/**
 * Onboarding wizard — Phase 7A.
 *
 * Mounted globally in App.js. Auto-opens on first login if the user
 * has no `onboarding_progress` document (or has one but is not complete
 * and not skipped). Skip button always visible top-right.
 *
 * State machine mirrored on the backend:
 *   GET   /api/onboarding/status
 *   POST  /api/onboarding/step/{n}/complete
 *   POST  /api/onboarding/skip
 *   POST  /api/onboarding/complete       (grants +100 credits + rep +5)
 */
export default function OnboardingWizard() {
    const { t } = useTranslation();
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [status, setStatus] = useState(null);
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [goal, setGoal] = useState(null);
    const [busy, setBusy] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [rewardGranted, setRewardGranted] = useState(false);

    // Load status on user change
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch("/onboarding/status");
                if (cancelled) return;
                setStatus(res);
                if (!res.is_complete && !res.is_skipped) {
                    setStep(res.current_step || 1);
                    setGoal((res.data && res.data.goal) || null);
                    setOpen(true);
                }
            } catch (e) {}
        })();
        return () => { cancelled = true; };
    }, [user]);

    const advance = useCallback(async (nextStep) => {
        if (busy) return;
        setBusy(true);
        try {
            await apiFetch(`/onboarding/step/${step}/complete`, {
                method: "POST",
                body: JSON.stringify({ data: goal ? { goal } : {} }),
            });
            if (nextStep > TOTAL_STEPS) {
                setCompleting(true);
                await apiFetch("/onboarding/step/8/complete", {
                    method: "POST", body: JSON.stringify({}),
                });
                const res = await apiFetch("/onboarding/complete", { method: "POST" });
                setRewardGranted(!res.already_rewarded);
                await refresh?.();
                return;
            }
            setStep(nextStep);
        } catch (e) {
            toast.error(e.message || "Onboarding save failed");
        } finally {
            setBusy(false);
        }
    }, [step, goal, busy, refresh]);

    const skip = async () => {
        try {
            await apiFetch("/onboarding/skip", { method: "POST" });
            setOpen(false);
            toast.info(t("onboarding.skip_toast"));
        } catch (e) {
            toast.error(e.message);
        }
    };

    const close = () => setOpen(false);

    if (!user || !open) return null;

    const progressPct = Math.round((step / TOTAL_STEPS) * 100);

    return (
        <AnimatePresence>
            <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm p-4 flex items-center justify-center"
            >
                <motion.div
                    key="modal"
                    initial={{ y: 20, opacity: 0, scale: 0.98 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: 20, opacity: 0, scale: 0.98 }}
                    transition={{ type: "spring", damping: 24, stiffness: 260 }}
                    data-testid="onboarding-wizard"
                    className="w-full max-w-2xl rounded-3xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl overflow-hidden"
                >
                    {/* Header + progress */}
                    <div className="border-b border-[var(--color-border)] px-6 py-4 flex items-center justify-between gap-4">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                                <span className="text-[11px] uppercase tracking-widest text-text-secondary">
                                    {t("onboarding.step_of", { current: step, total: TOTAL_STEPS })}
                                </span>
                                <span className="text-[11px] font-medium text-[var(--color-secondary)] tabular-nums">
                                    {progressPct}%
                                </span>
                            </div>
                            <div
                                data-testid="onboarding-progress-bar"
                                className="h-1.5 rounded-full bg-[var(--color-panel-ai)] overflow-hidden"
                                role="progressbar"
                                aria-valuenow={progressPct}
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-label={t("onboarding.step_of", { current: step, total: TOTAL_STEPS })}
                            >
                                <motion.div
                                    className="h-full bg-white hover:bg-[#EDEDED]"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progressPct}%` }}
                                    transition={{ duration: 0.3 }}
                                />
                            </div>
                        </div>
                        {!completing && (
                            <button
                                onClick={skip}
                                data-testid="onboarding-skip"
                                className="text-xs text-text-secondary hover:text-text-main transition-colors flex items-center gap-1.5"
                            >
                                {t("onboarding.skip")}
                                <X className="h-3 w-3" />
                            </button>
                        )}
                    </div>

                    {/* Body */}
                    <div className="px-6 md:px-10 py-8 min-h-[380px]">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={completing ? "finish" : step}
                                initial={{ opacity: 0, x: 24 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -24 }}
                                transition={{ duration: 0.25 }}
                            >
                                {completing ? (
                                    <StepFinish granted={rewardGranted} onClose={() => {
                                        setOpen(false);
                                        nav("/dashboard");
                                    }} />
                                ) : (
                                    <StepContent
                                        step={step}
                                        goal={goal}
                                        setGoal={setGoal}
                                        userPlan={user?.plan_id}
                                    />
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Footer */}
                    {!completing && (
                        <div className="border-t border-[var(--color-border)] px-6 py-4 flex justify-between items-center">
                            <button
                                data-testid="onboarding-back"
                                onClick={() => setStep((s) => Math.max(1, s - 1))}
                                disabled={step === 1 || busy}
                                className="text-xs px-4 py-2 text-text-secondary hover:text-text-main disabled:opacity-40 flex items-center gap-1.5"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" />
                                {t("common.back")}
                            </button>
                            <button
                                data-testid="onboarding-next"
                                onClick={() => advance(step + 1)}
                                disabled={busy}
                                className="text-sm rounded-full bg-white hover:bg-[#EDEDED] text-black px-6 py-2 font-medium hover:opacity-90 flex items-center gap-1.5 disabled:opacity-60"
                            >
                                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                {step === TOTAL_STEPS ? t("onboarding.complete_cta") : t("common.next")}
                                {!busy && <ArrowRight className="h-3.5 w-3.5" />}
                            </button>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

function StepContent({ step, goal, setGoal, userPlan }) {
    const { t } = useTranslation();
    const nav = useNavigate();
    // Skip step 6 (upgrade psych) if user is already on pro/business/enterprise
    if (step === 6 && ["pro", "business", "enterprise"].includes(userPlan || "")) {
        return <StepBonusAlreadyPro />;
    }
    switch (step) {
        case 1: return <StepWelcome />;
        case 2: return <StepGoal goal={goal} setGoal={setGoal} />;
        case 3: return <StepLeadEngine />;
        case 4: return <StepTemplate />;
        case 5: return <StepMarketplace />;
        case 6: return <StepUpgrade />;
        case 7: return <StepUniverse />;
        case 8: return <StepReady userPlan={userPlan} />;
        default: return null;
    }
}

function StepIntro({ icon: Icon, title, body, children }) {
    return (
        <div>
            <span className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-[var(--color-panel-ai)] border border-[var(--color-border)] mb-5">
                <Icon className="h-5 w-5 text-[var(--color-secondary)]" />
            </span>
            <h2 className="text-2xl md:text-3xl font-semibold mb-3">{title}</h2>
            <p className="text-sm text-text-secondary leading-relaxed max-w-lg">{body}</p>
            {children && <div className="mt-6">{children}</div>}
        </div>
    );
}

function StepWelcome() {
    const { t } = useTranslation();
    return (
        <StepIntro icon={Sparkles} title={t("onboarding.step1.title")} body={t("onboarding.step1.body")}>
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-5 flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-white hover:bg-[#EDEDED] grid place-items-center text-2xl">
                    🎁
                </div>
                <div>
                    <p className="text-sm font-medium">{t("onboarding.step1.bonus_title")}</p>
                    <p className="text-xs text-text-secondary">{t("onboarding.step1.bonus_body")}</p>
                </div>
            </div>
        </StepIntro>
    );
}

function StepGoal({ goal, setGoal }) {
    const { t } = useTranslation();
    const goals = [
        { key: "find_leads", label: t("onboarding.step2.goal_find_leads") },
        { key: "automate_outreach", label: t("onboarding.step2.goal_automate") },
        { key: "learn_ai", label: t("onboarding.step2.goal_learn") },
        { key: "just_exploring", label: t("onboarding.step2.goal_explore") },
    ];
    return (
        <StepIntro icon={Target} title={t("onboarding.step2.title")} body={t("onboarding.step2.body")}>
            <div className="grid gap-2 max-w-md">
                {goals.map((g) => (
                    <button
                        key={g.key}
                        onClick={() => setGoal(g.key)}
                        data-testid={`onboarding-goal-${g.key}`}
                        className={`rounded-xl border px-4 py-3 text-left text-sm transition-all ${
                            goal === g.key
                                ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10"
                                : "border-[var(--color-border)] bg-[var(--color-panel-ai)] hover:border-[var(--color-primary)]/40"
                        }`}
                    >
                        <span className="flex items-center gap-2">
                            {goal === g.key && <Check className="h-3.5 w-3.5 text-[var(--color-secondary)]" />}
                            {g.label}
                        </span>
                    </button>
                ))}
            </div>
        </StepIntro>
    );
}

function StepLeadEngine() {
    const { t } = useTranslation();
    const nav = useNavigate();
    return (
        <StepIntro icon={Target} title={t("onboarding.step3.title")} body={t("onboarding.step3.body")}>
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-5">
                <p className="text-xs uppercase tracking-widest text-text-secondary mb-2">Preview</p>
                <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between"><span className="text-text-secondary">Industry</span><span>SaaS · Sales</span></div>
                    <div className="flex items-center justify-between"><span className="text-text-secondary">Company size</span><span>10-50 employees</span></div>
                    <div className="flex items-center justify-between"><span className="text-text-secondary">Batch size</span><span>10 leads</span></div>
                </div>
                <button
                    onClick={() => nav("/leads")}
                    data-testid="onboarding-lead-engine-cta"
                    className="mt-4 text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium hover:opacity-90"
                >
                    {t("onboarding.step3.cta")} <ArrowRight className="h-3 w-3 inline ml-1" />
                </button>
            </div>
        </StepIntro>
    );
}

function StepTemplate() {
    const { t } = useTranslation();
    const nav = useNavigate();
    return (
        <StepIntro icon={Send} title={t("onboarding.step4.title")} body={t("onboarding.step4.body")}>
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-5">
                <div className="text-xs text-text-secondary mb-1">Subject</div>
                <div className="text-sm mb-3 font-mono">Quick idea for {`{{company}}`}</div>
                <div className="text-xs text-text-secondary mb-1">Body preview</div>
                <div className="text-sm text-text-main/90 leading-relaxed">
                    Hi {`{{first_name}}`}, noticed {`{{company}}`} is scaling — most teams at your stage struggle with pipeline consistency. Worth 15 min next week?
                </div>
                <button
                    onClick={() => nav("/outreach")}
                    data-testid="onboarding-template-cta"
                    className="mt-4 text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium hover:opacity-90"
                >
                    {t("onboarding.step4.cta")} <ArrowRight className="h-3 w-3 inline ml-1" />
                </button>
            </div>
        </StepIntro>
    );
}

function StepMarketplace() {
    const { t } = useTranslation();
    const nav = useNavigate();
    return (
        <StepIntro icon={Package} title={t("onboarding.step5.title")} body={t("onboarding.step5.body")}>
            <div className="rounded-2xl border border-[var(--color-primary)]/30 bg-gradient-to-br from-[var(--color-primary)]/8 to-[var(--color-secondary)]/8 p-5">
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-white hover:bg-[#EDEDED] text-black">BUNDLE</span>
                    <span className="text-sm font-medium">ETF Creator Pack</span>
                </div>
                <p className="text-xs text-text-secondary">3 assets — template pack + follow-up sequence + AI rewriter — 15% off vs. individual.</p>
                <button
                    onClick={() => nav("/marketplace/etf-creator-pack")}
                    data-testid="onboarding-marketplace-cta"
                    className="mt-4 text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium hover:opacity-90"
                >
                    {t("onboarding.step5.cta")} <ArrowRight className="h-3 w-3 inline ml-1" />
                </button>
            </div>
        </StepIntro>
    );
}

function StepUpgrade() {
    const { t } = useTranslation();
    const nav = useNavigate();
    return (
        <StepIntro icon={Sparkles} title={t("onboarding.step6.title")} body={t("onboarding.step6.body")}>
            <ul className="space-y-2 text-sm text-text-main/90 max-w-md">
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[var(--color-success)]" />5,000 credits / month vs. 300</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[var(--color-success)]" />Automated outreach campaigns unlocked</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[var(--color-success)]" />Custom modules access</li>
                <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[var(--color-success)]" />Hourly cap 500 credits/hour vs. 100</li>
            </ul>
            <div className="mt-4 flex gap-2">
                <button
                    onClick={() => nav("/app/subscription")}
                    data-testid="onboarding-upgrade-cta"
                    className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium hover:opacity-90"
                >
                    {t("onboarding.step6.cta")} <ArrowRight className="h-3 w-3 inline ml-1" />
                </button>
                <span className="text-xs text-text-secondary self-center">{t("onboarding.step6.not_now")}</span>
            </div>
        </StepIntro>
    );
}

function StepBonusAlreadyPro() {
    const { t } = useTranslation();
    return (
        <StepIntro icon={Sparkles} title={t("onboarding.step6_paid.title")} body={t("onboarding.step6_paid.body")}>
            <div className="rounded-xl border border-[var(--color-success)]/40 bg-[var(--color-success)]/5 p-4 text-sm">
                <CheckCircle2 className="h-4 w-4 text-[var(--color-success)] inline mr-1.5" />
                {t("onboarding.step6_paid.reward_ok")}
            </div>
        </StepIntro>
    );
}

function StepUniverse() {
    const { t } = useTranslation();
    const nav = useNavigate();
    return (
        <StepIntro icon={Globe} title={t("onboarding.step7.title")} body={t("onboarding.step7.body")}>
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-6 text-center">
                <span className="text-4xl">🌌</span>
                <p className="text-xs text-text-secondary mt-2 max-w-sm mx-auto">
                    Every campaign, world and reputation event lives here as a graph. Track evolution over time.
                </p>
                <button
                    onClick={() => nav("/universe")}
                    data-testid="onboarding-universe-cta"
                    className="mt-4 text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium hover:opacity-90"
                >
                    {t("onboarding.step7.cta")} <ArrowRight className="h-3 w-3 inline ml-1" />
                </button>
            </div>
        </StepIntro>
    );
}

function StepReady({ userPlan }) {
    const { t } = useTranslation();
    const [upgrading, setUpgrading] = useState(false);
    const eligible = ["free", "starter"].includes(userPlan || "free");

    const oneClickUpgrade = async () => {
        setUpgrading(true);
        try {
            const res = await apiFetch("/billing/subscription-checkout", {
                method: "POST",
                body: JSON.stringify({
                    plan_id: "pro",
                    interval: "monthly",
                    coupon_code: "onboarding_20",
                }),
            });
            if (res && res.url) {
                window.location.href = res.url;
                return;
            }
            toast.error("Checkout unavailable, please retry.");
        } catch (e) {
            toast.error(e.message || "Upgrade failed");
        } finally {
            setUpgrading(false);
        }
    };

    return (
        <StepIntro icon={PartyPopper} title={t("onboarding.step8.title")} body={t("onboarding.step8.body")}>
            <div className="rounded-2xl border border-[var(--color-primary)]/30 bg-gradient-to-br from-[var(--color-primary)]/10 to-[var(--color-secondary)]/10 p-6 mb-4">
                <div className="text-4xl mb-2">🎉</div>
                <p className="text-sm">
                    Click <strong>{t("onboarding.complete_cta")}</strong> to unlock <strong>100 bonus credits</strong> and start using Global Lead Suite.
                </p>
            </div>

            {eligible && (
                <div
                    data-testid="onboarding-step8-upgrade-card"
                    className="rounded-2xl border border-[var(--color-secondary)]/40 bg-[var(--color-panel-ai)] p-5"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white hover:bg-[#EDEDED]">
                            <Rocket className="h-4 w-4 text-white" />
                        </span>
                        <div>
                            <div className="text-sm font-semibold text-text-main">
                                {t("onboarding_upgrade.title")}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-text-secondary">
                                {t("onboarding_upgrade.subtitle")}
                            </div>
                        </div>
                        <span className="ml-auto text-[10px] font-bold tracking-widest px-2 py-1 rounded bg-white hover:bg-[#EDEDED] text-black">
                            {t("onboarding_upgrade.coupon_label")}
                        </span>
                    </div>
                    <ul className="space-y-1.5 text-xs text-text-main/90 mb-4">
                        <li className="flex items-center gap-2"><Check className="h-3 w-3 text-[var(--color-success)]" />{t("onboarding_upgrade.bullets_1")}</li>
                        <li className="flex items-center gap-2"><Check className="h-3 w-3 text-[var(--color-success)]" />{t("onboarding_upgrade.bullets_2")}</li>
                        <li className="flex items-center gap-2"><Check className="h-3 w-3 text-[var(--color-success)]" />{t("onboarding_upgrade.bullets_3")}</li>
                    </ul>
                    <button
                        data-testid="onboarding-step8-upgrade-btn"
                        onClick={oneClickUpgrade}
                        disabled={upgrading}
                        className="w-full text-sm rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2.5 font-medium hover:opacity-90 flex items-center justify-center gap-1.5 disabled:opacity-60"
                    >
                        {upgrading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        {t("onboarding_upgrade.cta")}
                        {!upgrading && <ArrowRight className="h-3.5 w-3.5" />}
                    </button>
                    <p className="mt-2 text-[11px] text-text-secondary text-center">
                        {t("onboarding_upgrade.note")}
                    </p>
                </div>
            )}
        </StepIntro>
    );
}

function StepFinish({ granted, onClose }) {
    const { t } = useTranslation();
    return (
        <div className="text-center py-10" data-testid="onboarding-finish">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl md:text-3xl font-semibold mb-2">{t("onboarding.finish.title")}</h2>
            <p className="text-sm text-text-secondary max-w-md mx-auto mb-2">
                {granted ? t("onboarding.finish.body_granted") : t("onboarding.finish.body_already")}
            </p>
            <button
                onClick={onClose}
                data-testid="onboarding-finish-cta"
                className="mt-6 text-sm rounded-full bg-white hover:bg-[#EDEDED] text-black px-6 py-2 font-medium hover:opacity-90"
            >
                {t("onboarding.finish.cta")}
                <ArrowRight className="h-3.5 w-3.5 inline ml-1.5" />
            </button>
        </div>
    );
}
